import React, { ReactNode, useState, useEffect, useRef } from 'react';
import {
  Users,
  Building2,
  Calendar,
  Activity,
  BarChart3,
  Settings,
  FileText,
  Search,
  User,
  Shield,
  Bell,
  Mail,
  DollarSign,
  FolderOpen,
  Wrench,
  Video,
  Archive,
  MessageSquare,
  TrendingUp,
  Target,
  Menu,
  X,
  XCircle,
  Phone
} from 'lucide-react';
import NotificationCenter from './NotificationCenter';
import VersionDisplay from './VersionDisplay';
import ConnectionStatusIndicator from './ConnectionStatusIndicator';
import Avatar from './Avatar';
import authService from '../services/authService';
import { usePermissions } from '../hooks/usePermissions';
import { APP_BRAND } from '../constants/branding';


interface LayoutProps {
  children: ReactNode;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onLogout: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, activeTab, onTabChange, onLogout }) => {
  const session = authService.getCurrentSession();
  const user = session?.user;
  const { user: userWithRole, isAdmin, isSuperAdmin, isCollaboradorProgramador, isCollaboradorComercial, loading: permissionsLoading } = usePermissions();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userHasToggled = useRef(false);

  // Auto-collapse sidebar only on initial load for very small screens
  // Does not override user's manual toggle
  useEffect(() => {
    const handleResize = () => {
      if (userHasToggled.current) return;
      if (window.innerWidth < 900) {
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!user) {
    return <div>Error: No hay sesión de usuario</div>;
  }

  // Etiqueta de rol para mostrar en UI
  const getRoleLabel = () => {
    if (permissionsLoading) return 'Cargando...';
    if (!userWithRole?.user_role) return 'Usuario';
    return userWithRole.user_role.display_name || 'Usuario';
  };

  const roleLabel = getRoleLabel();

  // Construir menú de navegación basado en el rol
  const buildNavItems = () => {
    // Colaborador Programador: Solo ve Panel, Mi Calendario, Notificaciones
    if (isCollaboradorProgramador()) {
      return [
        { id: 'pagos', label: 'Panel', icon: Users },
        { id: 'gestion-proyectos', label: 'Mis proyectos', icon: FolderOpen },
        { id: 'mis-mantenimientos', label: 'Mis mantenimientos', icon: Wrench },
        { id: 'reuniones', label: 'Mi Calendario', icon: Calendar },
        { id: 'notificaciones', label: 'Notificaciones', icon: Bell }
      ];
    }

    // Colaborador Comercial: Ve leads, contactos (personas+empresas), actividades, reuniones
    if (isCollaboradorComercial()) {
      return [
        { id: 'leads', label: 'Leads', icon: Users },
        { id: 'contactos', label: 'Clientes', icon: Users },
        { id: 'actividades', label: 'Actividades', icon: Activity },
        { id: 'reuniones', label: 'Mi Calendario', icon: Calendar },
        { id: 'notificaciones', label: 'Notificaciones', icon: Bell }
      ];
    }

    // Admin y Superadmin: Menú completo.
    // "Empresas" ya no es tab: personas y empresas viven en el mismo módulo
    // "Clientes" con toggle interno.
    return [
      { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
      { id: 'leads', label: 'Leads', icon: Users },
      { id: 'leads-descartados', label: 'Leads Descartados', icon: XCircle },
      { id: 'leads-archivados', label: 'Leads Archivados', icon: Archive },
      { id: 'contactos', label: 'Clientes', icon: Users },
      ...(isAdmin() ? [
        { id: 'usuarios', label: 'Usuarios', icon: Shield }
      ] : []),
      { id: 'actividades', label: 'Actividades', icon: Activity },
      { id: 'calendly', label: 'Calendly', icon: Calendar },
      { id: 'reuniones', label: isAdmin() ? 'Calendario' : 'Mi Calendario', icon: Calendar },
      { id: 'conversaciones-ia', label: 'Conversaciones IA', icon: MessageSquare },
      { id: 'presupuestos', label: 'Presupuestos', icon: DollarSign },
      { id: 'gestion-proyectos', label: 'Gestión de Proyectos', icon: FolderOpen },
      { id: 'mantenimiento', label: 'Mantenimiento', icon: Wrench },
      { id: 'pagos', label: 'Plantilla', icon: Users },
      { id: 'fathom', label: 'Fathom', icon: Video },
      { id: 'prospeccion-llamadas', label: 'Prospeccion', icon: Phone },
      { id: 'comercial', label: 'Comercial IA', icon: Target },
      { id: 'notificaciones', label: 'Notificaciones', icon: Bell },
      ...(isSuperAdmin() ? [
        { id: 'control-operativo', label: 'Control Operativo', icon: Activity },
        { id: 'whatsapp-soporte', label: 'Soporte WhatsApp', icon: MessageSquare }
      ] : []),
      ...(isSuperAdmin() ? [
        { id: 'historial-actividad', label: 'Historial de actividad', icon: Activity },
        { id: 'monitoreo', label: 'Monitoreo', icon: TrendingUp }
      ] : [])
    ];
  };

  const navItems = buildNavItems();

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop & Mobile */}
      <div className={`${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0 ${
        sidebarCollapsed ? 'md:w-16' : 'md:w-64'
      } w-64 fixed md:relative left-0 top-0 h-screen md:h-auto bg-white shadow-lg border-r border-gray-200 flex flex-col overflow-hidden transition-all duration-300 z-50`}>
        {/* Logo Header - Fixed */}
        <div className="flex-shrink-0 p-4 border-b border-gray-200">
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-2'}`}>
            <img
              src={APP_BRAND.logoPath}
              alt={APP_BRAND.logoAlt}
              className="w-7 h-7 rounded-lg object-contain flex-shrink-0"
            />
            {!sidebarCollapsed && (
              <div className="min-w-0">
                <h1 className="text-base font-bold text-gray-900 truncate">{APP_BRAND.name}</h1>
                <p className="text-xs text-gray-500">{APP_BRAND.tagline}</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {navItems.map((item) => (
            <div key={item.id} className="relative group">
              <button
                onClick={() => {
                  onTabChange(item.id);
                  if (window.innerWidth < 768) {
                    setIsMobileMenuOpen(false);
                  }
                }}
                className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-2'} px-3 py-2 rounded-lg text-left transition-all duration-200 mb-1 ${
                  activeTab === item.id
                    ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600 pl-2'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                title={sidebarCollapsed ? item.label : ''}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                {!sidebarCollapsed && <span className="text-sm font-medium truncate">{item.label}</span>}
              </button>
              {sidebarCollapsed && (
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                  {item.label}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Bottom Section - Fixed */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-white">
          {isAdmin() && (
            <div className="px-3 py-2 space-y-1">
              <div className="relative group">
                <button
                  onClick={() => {
                    onTabChange('configuracion');
                    if (window.innerWidth < 768) {
                      setIsMobileMenuOpen(false);
                    }
                  }}
                  className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-2'} px-3 py-2 rounded-lg transition-colors ${
                    activeTab === 'configuracion'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  title={sidebarCollapsed ? 'Configuración' : ''}
                >
                  <Settings className="w-4 h-4 flex-shrink-0" />
                  {!sidebarCollapsed && <span className="text-sm truncate">Configuración</span>}
                </button>
                {sidebarCollapsed && (
                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                    Configuración
                  </div>
                )}
              </div>
            </div>
          )}
          {!sidebarCollapsed && (
            <div className="px-4 py-3 space-y-1">
              <div className="text-xs text-gray-500 truncate">
                <span className="font-medium">{user.name}</span>
              </div>
              <div className="text-xs text-gray-400 truncate">
                {roleLabel}
              </div>
              <VersionDisplay position="sidebar" showDetails={false} />
            </div>
          )}
          {sidebarCollapsed && (
            <div className="px-2 py-3 flex justify-center">
              <div className="relative group">
                <button
                  type="button"
                  onClick={() => onTabChange('mi-perfil')}
                  className="cursor-pointer"
                  title="Mi Perfil"
                >
                  <Avatar name={user.name} src={(user as any).avatar} size="sm" />
                </button>
                <div className="absolute left-full ml-2 bottom-0 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                  {user.name}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 lg:px-6 py-3 lg:py-4">
          <div className="flex items-center justify-between gap-2 lg:gap-4">
            {/* Hamburger + Search */}
            <div className="flex items-center gap-2 lg:gap-4 flex-1">
              {/* Hamburger Menu Button */}
              <button
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setIsMobileMenuOpen(!isMobileMenuOpen);
                  } else {
                    userHasToggled.current = true;
                    setSidebarCollapsed(!sidebarCollapsed);
                  }
                }}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title={sidebarCollapsed ? 'Expandir menú' : 'Contraer menú'}
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Search Bar */}
              <div className="flex-1 max-w-xs lg:max-w-lg">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 lg:w-5 lg:h-5" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    className="w-full pl-9 lg:pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-1 md:space-x-2 lg:space-x-3">
              <NotificationCenter onNavigate={onTabChange} />
              <ConnectionStatusIndicator position="header" showDetails={false} />
              <a
                href="https://propuestas.ceroideas.es/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-3 lg:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                title="Presupuestos"
              >
                <DollarSign className="w-4 h-4 lg:w-5 lg:h-5" />
                <span className="text-sm font-medium hidden lg:inline">Presupuestos</span>
              </a>
              <button
                onClick={() => onTabChange('mi-perfil')}
                className="hidden lg:flex items-center space-x-2 p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Avatar name={user.name} src={(user as any).avatar} size="sm" />
                <div className="text-left hidden lg:block">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs text-gray-500">Mi Perfil</div>
                </div>
              </button>
              <button
                onClick={onLogout}
                className="flex items-center space-x-2 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Cerrar sesión"
              >
                <span className="text-sm font-medium hidden lg:inline">Salir</span>
                <span className="text-sm font-medium lg:hidden">✕</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto min-w-0">
          {children}

          {/* Footer with version */}
          <VersionDisplay position="footer" />
        </main>
      </div>
    </div>
  );
};

export default Layout;
